import './LearningApp.less';
import React, {useState, useEffect, useContext, useMemo} from 'react';
import {useParams, useHistory} from 'react-router-dom';
import useCourse from "../../hooks/useCourse";
import LoadingPlaceholder from "../../components/loading-placeholder/LoadingPlaceholder";
import Menu from "./menu/Menu";
import routes from "../../routing/routes";
import ErrorContext from "../../contexts/error/ErrorContext";
import DraftView from "../../components/draft-view/DraftView";
import {jsonToObject} from "../../../bootstrap/utils/json";
import VideoPlayer from "./video-player/VideoPlayer";
import AudioPlayer from "./audio-player/AudioPlayer";
import DocumentDownloader from "./document-downloader/DocumentDownloader";
import useBreadcrumb from "../../hooks/useBreadcrumb";
import MediaQueryDisplay from "../../components/media-query-display/MediaQueryDisplay";
import screenSizeQueries from "../../styles/screenSizeQueries";
import TitleBar from "./title-bar/TitleBar";
import QuizPlayer from "../../players/quiz/QuizPlayer";
import Popup from '../../../bootstrap/components/popup/Popup';
import __ from '../../i18n';
import {NavBar} from './nav-bar/NavBar';


export default function LearningApp() {
    const params = useParams();

    const courseId = parseInt(params.courseId);

    const lessonId = parseInt(params.lessonId);

    const [course, refs, isLoading] = useCourse(courseId, [
        'chapter',
        'lesson',
        'video',
        'audio',
        'document',
        'quiz',
        'question',
        'question_group',
    ]);

    const {chapters, lessons, videos, audios, documents, quizzes, questions, questionGroups} = refs;

    const history = useHistory();

    const errorContext = useContext(ErrorContext);

    const [showsMenu, setShowsMenu] = useState(false);

    const firstLesson = useMemo(() => {
        if (lessons == null || chapters == null) {
            return null;
        }
        let firstLesson = null, lastLesson = null;
        for (let i = 0; i < chapters.length + 1; i++) {
            const chapterId = i < chapters.length ? chapters[i].id : null;
            lessons.forEach((lesson) => {
                if (lesson.chapter_id === chapterId) {
                    if (lastLesson !== null) {
                        lastLesson.next_ = lesson;
                    } else {
                        lesson.next_ = null;
                        lesson.prev_ = null;
                        firstLesson = lesson;
                    }
                    lesson.prev_ = lastLesson;
                    lastLesson = lesson;
                }
            });
        }
        return firstLesson;
    }, [lessons, chapters]);

    const lesson = useMemo(() => {
        if (lessonId !== 0 && lessons && lessons.length > 0) {
            const lesson = lessons.find(t => t.id === lessonId);
            if (lesson == null) {
                errorContext.setError('NotFound');
            }
            return lesson;
        }
        return null;
    }, [lessonId, lessons, errorContext]);

    useEffect(() => {
        if (lessonId === 0 && firstLesson !== null) {
            history.replace(routes.learning.getUrl(courseId, firstLesson.id));
        }
    }, [courseId, lessonId, firstLesson]);

    useBreadcrumb(course === null ? [] : [
        {name: course.name, link: routes.learning.getUrl(courseId, lessonId)},
    ], [course, lessonId]);

    if (isLoading) {
        return <LoadingPlaceholder/>;
    }

    if (course === null) {
        return null;
    }

    if (lesson === null) {
        return null;
    }

    const chapter = chapters.find(t => t.id === lesson.chapter_id) || null;

    const video = lesson.video_id !== null ? videos.find(t => t.id === lesson.video_id) : null;
    const audio = lesson.audio_id !== null ? audios.find(t => t.id === lesson.audio_id) : null;
    const document = lesson.document_id !== null ? documents.find(t => t.id === lesson.document_id) : null;
    const quiz = lesson.quiz_id !== null ? quizzes.find(t => t.id === lesson.quiz_id) : null;
    const textContentState = jsonToObject(lesson.text_content);

    return <div className="learning-app">
        <MediaQueryDisplay query={screenSizeQueries.desktop}>
            <Menu
                chapters={chapters}
                lessons={lessons}
                currentLessonId={lessonId}
            />
        </MediaQueryDisplay>
        <MediaQueryDisplay excludes query={screenSizeQueries.desktop}>
            <TitleBar
                course={course}
                setShowsMenu={setShowsMenu}
            />
        </MediaQueryDisplay>
        <div className="main-col">
            <div className="header">
                {
                    chapter !== null &&
                    <div className="chapter">{chapter.name}</div>
                }
                <h1 className="title">{lesson.name}</h1>
            </div>
            <div className="body">
                <div className="inner">
                    {
                        video !== null &&
                        <VideoPlayer video={video}/>
                    }
                    {
                        audio !== null &&
                        <AudioPlayer audio={audio}/>
                    }
                    {
                        textContentState !== null &&
                        <DraftView contentState={textContentState}/>
                    }
                    {
                        quiz !== null &&
                        <QuizPlayer
                            questions={questions.filter(q => q.quiz_id === quiz.id)}
                            questionGroups={questionGroups.filter(q => q.quiz_id === quiz.id)}
                        />
                    }
                    {
                        document !== null &&
                        <DocumentDownloader document={document}/>
                    }
                </div>
            </div>
            <div className="footer">
                <NavBar
                    navPrev={lesson.prev_ ? (() => {
                        history.replace(routes.learning.getUrl(courseId, lesson.prev_.id));
                    }) : null}
                    navNext={lesson.next_ ? (() => {
                        history.replace(routes.learning.getUrl(courseId, lesson.next_.id));
                    }) : null}
                    setShowsMenu={setShowsMenu}
                />
            </div>
        </div>
        {
            showsMenu &&
            <Popup
                name="learning-app"
                title={__('Lessons list')}
                close={() => setShowsMenu(false)}
            >
                <Menu
                    chapters={chapters}
                    lessons={lessons}
                    currentLessonId={lessonId}
                />
            </Popup>
        }
    </div>;
}
